const RECOMMENDATIONS_SECTION_ID = 'cart-recommendations';
const MAX_REQUEST_LIMIT = 10;

// Keyed on the request URL, shared across every instance and every re-render of the cart drawer, so that reopening
// the drawer or changing a line item quantity never refetches a seed we already know the answer for.
const requests = new Map();

function fetchRecommendations(productId, intent, limit) {
  const url = `${window.Shopify.routes.root}recommendations/products?product_id=${productId}&limit=${limit}&intent=${intent}&section_id=${RECOMMENDATIONS_SECTION_ID}`;

  if (!requests.has(url)) {
    requests.set(
      url,
      fetch(url, { priority: 'low' })
        .then((response) => (response.ok ? response.text() : ''))
        .catch(() => '')
    );
  }

  return requests.get(url);
}

function parseCards(html) {
  if (!html) {
    return [];
  }

  const doc = new DOMParser().parseFromString(html, 'text/html');
  return Array.from(doc.querySelectorAll(`#${RECOMMENDATIONS_SECTION_ID} .horizontal-product`));
}

function productHandle(card) {
  const url = card.querySelector('a[href*="/products/"]')?.getAttribute('href') || '';
  return url.match(/\/products\/([^/?#]+)/)?.[1] || null;
}

/**
 * Take one card from each seed in turn, so that a cart of several products gets suggestions for all of them rather
 * than filling every slot from the first seed.
 */
function interleave(groups) {
  const merged = [];
  const longest = Math.max(0, ...groups.map((group) => group.length));

  for (let index = 0; index < longest; index++) {
    groups.forEach((group) => group[index] && merged.push(group[index]));
  }

  return merged;
}

function onIdle(callback) {
  if (window.requestIdleCallback) {
    window.requestIdleCallback(callback, { timeout: 2000 });
  } else {
    setTimeout(callback, 500);
  }
}

class CartRecommendations extends HTMLElement {
  connectedCallback() {
    this.headerElement = this.querySelector('[data-recommendations-header]');
    this.navElement = this.querySelector('[data-recommendations-nav]');
    this.navTemplate = this.querySelector('[data-recommendations-nav-template]');
    this.resultsElement = this.querySelector('[data-recommendations-results]');
    this.fallbackTemplate = this.querySelector('[data-recommendations-fallback]');

    if (!this.resultsElement) {
      return;
    }

    if (this.seeds.length === 0) {
      this._renderFallback();
    } else if (this.closest('[open]')) {
      this._load();
    } else {
      onIdle(() => this.isConnected && this._load());
    }
  }

  get seeds() {
    return (this.getAttribute('seeds') || '').split(',').filter(Boolean);
  }

  get excludedHandles() {
    return (this.getAttribute('exclude') || '').split(',').filter(Boolean);
  }

  get intents() {
    const intent = this.getAttribute('intent');
    return intent === 'auto' ? ['complementary', 'related'] : [intent || 'related'];
  }

  get limit() {
    return parseInt(this.getAttribute('limit') || 4);
  }

  get blends() {
    return this.getAttribute('blends') !== 'false';
  }

  async _load() {
    if (this._isLoaded) {
      return;
    }

    this._isLoaded = true;

    // Ask for enough products to survive filtering out everything already in the cart.
    const requestLimit = Math.min(MAX_REQUEST_LIMIT, this.limit + this.excludedHandles.length);
    const groups = await Promise.all(this.seeds.map((seed) => this._loadSeed(seed, requestLimit)));

    if (!this.isConnected) {
      return;
    }

    const seen = new Set(this.excludedHandles);
    const cards = [];

    for (const card of interleave(groups)) {
      const handle = productHandle(card);

      if (!handle || seen.has(handle)) {
        continue;
      }

      seen.add(handle);
      cards.push(card);

      if (cards.length === this.limit) {
        break;
      }
    }

    if (cards.length === 0) {
      this._renderFallback();
    } else {
      this._render(cards);
    }
  }

  /**
   * Complementary products are curated in Search & Discovery and may not be set up on every product, so when the
   * intent is "auto" we fall back to Shopify's algorithmic related products for that seed.
   */
  async _loadSeed(productId, limit) {
    for (const intent of this.intents) {
      const cards = parseCards(await fetchRecommendations(productId, intent, limit));

      if (cards.length > 0) {
        return cards;
      }
    }

    return [];
  }

  _renderFallback() {
    const cards = Array.from(this.fallbackTemplate?.content.querySelectorAll('.horizontal-product') || []);

    if (cards.length > 0) {
      this._render(cards);
    }
  }

  _render(cards) {
    // Unhide before inserting the carousel: it measures its items on connection, which needs a laid out container.
    this.hidden = false;
    this.headerElement.hidden = false;

    const list = document.createElement('div');
    list.className = `horizontal-product-list ${this.blends ? 'divide-x' : 'separate'}`;
    list.append(...cards.map((card) => document.importNode(card, true)));

    const carousel = document.createElement('scroll-carousel');
    carousel.id = this.getAttribute('carousel-id');
    carousel.setAttribute('selector', '.horizontal-product');
    carousel.className = `horizontal-product-list-carousel ${this.blends ? '' : 'separate '}scroll-area bleed`;
    carousel.append(list);

    this.resultsElement.replaceChildren(carousel);

    // The carousel must already be in the document when the buttons connect, as they resolve their aria-controls
    // target at that moment to track its scroll position.
    if (cards.length > 1 && this.navTemplate) {
      this.navElement.replaceChildren(this.navTemplate.content.cloneNode(true));
    }
  }
}

if (!window.customElements.get('cart-recommendations')) {
  window.customElements.define('cart-recommendations', CartRecommendations);
}
